import React, { useState } from "react";
import { Contact, User } from "lucide-react";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import CopyPhoneButton from "@/components/common/CopyPhoneButton";import {
  getAgentNameForRecord,
  isCustomerPhone,
} from "@/utils/callPartyHelpers";
import { DEFAULT_LEAD_STATUSES, LEAD_TONE_DOT, getLeadStatusMeta } from "@/utils/crmHelpers";

const CallPartyPhone = ({
  record,
  phone,
  agents = [],
  isDark,
  leadStatuses = DEFAULT_LEAD_STATUSES,
  customerProfile,
  customerProfileLoading = false,
  onCustomerClick,
  onHoverRequest,
  showRoleLabel = false,
}) => {
  if (!phone) {
    return <span className={isDark ? "text-slate-500" : "text-gray-400"}>-</span>;
  }

  const isCustomer = isCustomerPhone(record, phone, agents);
  const agentName = getAgentNameForRecord(record, agents);

  const roleLabel = isCustomer ? "Müşteri" : "Agent";
  const roleClass = isCustomer
    ? (isDark ? "text-violet-300 bg-violet-500/15 border-violet-500/25" : "text-violet-700 bg-violet-50 border-violet-200")
    : (isDark ? "text-cyan-300 bg-cyan-500/15 border-cyan-500/25" : "text-cyan-700 bg-cyan-50 border-cyan-200");

  if (isCustomer) {
    const name = customerProfile?.ad_soyad?.trim();
    const defaultLead = leadStatuses[0]?.value || "yeni";
    const leadMeta = getLeadStatusMeta(customerProfile?.lead_durumu, leadStatuses);
    const dotClass = LEAD_TONE_DOT[leadMeta?.tone] || LEAD_TONE_DOT.violet;

    const handleOpenChange = (open) => {
      if (open && onHoverRequest) onHoverRequest(phone);
    };

    return (
      <div className="space-y-1">
        {showRoleLabel && (
          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${roleClass}`}>
            <Contact className="w-3 h-3" />
            {roleLabel}
          </span>
        )}
        <Tooltip delayDuration={150} onOpenChange={handleOpenChange}>
          <TooltipTrigger asChild>
            <div className="inline-flex items-center gap-0.5 max-w-full">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onCustomerClick?.(e);
                }}
                className={`inline-flex items-center gap-1.5 max-w-full px-2 py-1 rounded text-left transition-all hover:ring-2 hover:ring-violet-400/50 ${
                  isDark ? "bg-violet-500/15 text-violet-300 border border-violet-500/25" : "bg-violet-50 text-violet-700 border border-violet-200"
                }`}
              >
                <Contact className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate text-xs font-medium font-mono">{phone}</span>
                {customerProfile?.lead_durumu && customerProfile.lead_durumu !== defaultLead && (
                  <span className={`shrink-0 w-1.5 h-1.5 rounded-full ${dotClass}`} />
                )}
              </button>
              <CopyPhoneButton phone={phone} isDark={isDark} />
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={6}
            className={`p-0 border shadow-xl ${isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-gray-200 text-gray-900"}`}
          >
            <div className="px-3 py-2.5 space-y-1">
              {customerProfileLoading ? (
                <>
                  <p className="text-xs font-semibold leading-none">Müşteri bilgisi</p>
                  <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Yükleniyor...</p>
                </>
              ) : name ? (
                <>
                  <p className="text-xs font-semibold leading-none">{name}</p>
                  <p className={`text-[10px] font-medium uppercase tracking-wider ${isDark ? "text-slate-500" : "text-gray-400"}`}>GSM</p>
                  <p className="font-mono text-sm">{phone}</p>
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold leading-none">Müşteri CRM</p>
                  <p className={`font-mono text-sm ${isDark ? "text-slate-400" : "text-gray-600"}`}>{phone}</p>
                </>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {showRoleLabel && (
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${roleClass}`}>
          <User className="w-3 h-3" />
          Agent{agentName ? ` · ${agentName}` : ""}
        </span>
      )}
      <button
        type="button"
        onClick={async (e) => {
          e.stopPropagation();
          try {
            await navigator.clipboard.writeText(String(phone));
            toast.success("Numara kopyalandı");
          } catch {
            toast.error("Kopyalanamadı");
          }
        }}
        title={agentName ? `${agentName} — kopyalamak için tıklayın` : "Kopyalamak için tıklayın"}
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded font-mono text-xs cursor-copy transition-colors ${
          isDark ? "bg-slate-800 text-slate-300 hover:bg-slate-700" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
      >
        {!showRoleLabel && <User className="w-3 h-3 shrink-0 opacity-70" />}
        {phone}
      </button>    </div>
  );
};

export default CallPartyPhone;
